import { Button, Form, Modal, Skeleton, Tabs } from "antd";
import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { Editor } from "react-draft-wysiwyg";
import {
  ContentState,
  convertFromHTML,
  convertToRaw,
  EditorState,
} from "draft-js";
import "react-draft-wysiwyg/dist/react-draft-wysiwyg.css";
import draftToHtml from "draftjs-to-html";
import Dashboard from "../Layout/Dashboard";
import {
  DeleteOutlined,
  EditOutlined,
  EyeOutlined,
  PaperClipOutlined,
} from "@ant-design/icons";
import { createStoreInfo, updateStoreInfo } from "../../actions/staticInfo";
import { getStore } from "../../actions/store";
import { formatDate } from "../../utils/Utils";

const StaticInfo = ({ currentUser }) => {
  const dispatch = useDispatch();
  const history = useNavigate();
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(true);
  const [loadingButton, setLoadingButton] = useState(false);
  const [previewVisible, setPreviewVisible] = useState(false);

  const [file, setFile] = useState("");
  const [previewFile, setPreviewFile] = useState();

  const [term, setTerm] = useState(() => EditorState.createEmpty());
  const [privacy, setPrivacy] = useState(() => EditorState.createEmpty());
  const [about, setAbout] = useState(() => EditorState.createEmpty());

  const staticInfo = useSelector((state) => state.store)?.store
    ?.staticInfo?.[0];

  useEffect(() => {
    setLoading(true);
    setLoadingButton(false);
    dispatch(getStore(currentUser?.store));
    setTimeout(() => {
      setLoading(false);
    }, 1000);
  }, []);

  const onFinish = () => {
    const rawContentStateTerm = convertToRaw(term.getCurrentContent());
    const rawContentStateAbout = convertToRaw(about.getCurrentContent());
    const rawContentStatePrivacy = convertToRaw(privacy.getCurrentContent());
    const markupTerm = draftToHtml(rawContentStateTerm);
    const markupAbout = draftToHtml(rawContentStateAbout);
    const markupPrivacy = draftToHtml(rawContentStatePrivacy);
    const productData = {
      termAndCondition: markupTerm,
      privacyPolicy: markupPrivacy,
      about: markupAbout,
      aboutImage: file,
      user: currentUser.id,
      store: currentUser.store,
    };
    setLoadingButton(true);
    let formData = new FormData();
    formData.append("termAndCondition", productData?.termAndCondition);
    formData.append("privacyPolicy", productData?.privacyPolicy);
    formData.append("about", productData?.about);
    formData.append("aboutImage", productData?.aboutImage);
    formData.append("user", productData?.user);
    formData.append("store", productData?.store);

    if (staticInfo) {
      dispatch(
        updateStoreInfo(staticInfo?._id, formData, history, setLoadingButton)
      );
    } else {
      dispatch(createStoreInfo(formData, history, setLoadingButton));
    }
  };

  useEffect(() => {
    setPrivacy(
      EditorState?.createWithContent(
        ContentState?.createFromBlockArray(
          convertFromHTML(
            staticInfo?.privacyPolicy ? staticInfo?.privacyPolicy : ""
          )
        )
      )
    );
    setTerm(
      EditorState?.createWithContent(
        ContentState?.createFromBlockArray(
          convertFromHTML(
            staticInfo?.termAndCondition ? staticInfo?.termAndCondition : ""
          )
        )
      )
    );
    setAbout(
      EditorState?.createWithContent(
        ContentState?.createFromBlockArray(
          convertFromHTML(staticInfo?.about ? staticInfo?.about : "")
        )
      )
    );
    setPreviewFile(staticInfo?.aboutImage);
  }, [staticInfo]);

  const items = [
    {
      key: "1",
      label: "Terms And Conditions",
      children: (
        <Skeleton loading={loading}>
          <div className="border">
            <div className="py-2 text-center border-b">
              <strong> Terms And Conditions</strong>
            </div>

            <div className="p-4">
              <Editor editorState={term} onEditorStateChange={setTerm} />
            </div>

            <div className="p-2 text-center border-t">
              <div className="flex items-center justify-between">
                <p>
                  Created At: <strong>{formatDate(staticInfo?.createdAt)}</strong>
                </p>
                <p>
                  Updated At: <strong>{formatDate(staticInfo?.updatedAt)}</strong>
                </p>
              </div>
            </div>
          </div>
        </Skeleton>
      ),
    },
    {
      key: "2",
      label: "Privacy Policy",
      children: (
        <Skeleton loading={loading}>
          <div className="border">
            <div className="py-2 text-center border-b">
              <strong>Privacy Policy</strong>
            </div>

            <div className="p-4">
              <Editor editorState={privacy} onEditorStateChange={setPrivacy} />
            </div>

            <div className="p-2 text-center border-t">
              <div className="flex items-center justify-between">
                <p>
                  Created At: <strong>{formatDate(staticInfo?.createdAt)}</strong>
                </p>
                <p>
                  Updated At: <strong>{formatDate(staticInfo?.updatedAt)}</strong>
                </p>
              </div>
            </div>
          </div>
        </Skeleton>
      ),
    },
    {
      key: "3",
      label: "About",
      children: (
        <Skeleton loading={loading}>
          {/* your full About content stays SAME here */}
          <div className="border">
            <div className="py-2 text-center border-b">
              <strong>About</strong>
            </div>

            <div className="p-4">
              <Editor editorState={about} onEditorStateChange={setAbout} />
            </div>

            <div className="p-2 text-center border-t">
              <div className="flex items-center justify-between">
                <p>
                  Created At: <strong>{formatDate(staticInfo?.createdAt)}</strong>
                </p>
                <p>
                  Updated At: <strong>{formatDate(staticInfo?.updatedAt)}</strong>
                </p>
              </div>
            </div>
          </div>

          {/* KEEP YOUR IMAGE SECTION AS IT IS */}
        </Skeleton>
      ),
    },
  ];

  return (
    <Dashboard title="Website Information">
      <div className="my-4">
        <Form
          layout="vertical"
          onFinish={onFinish}
          requiredMark={false}
          form={form}
          autoComplete="off"
        >
          <div className="flex items-center justify-between">
            <h2 className="text-2xl m-0 font-bold">Website Information</h2>

            <div className="flex flex-wrap justify-end gap-4">
              <Button onClick={() => history("/dashboard")}>
                Cancel
              </Button>

              <Button type="primary" htmlType="submit" loading={loadingButton}>
                {staticInfo ? "Update" : "Save"}
              </Button>
            </div>
          </div>

          <div className="my-4 bg-white shadow p-4">
            <Tabs defaultActiveKey="1" tabPosition="left" items={items} />
          </div>
        </Form>
      </div>
    </Dashboard>
  );
};

export default StaticInfo;
